import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import Column from 'antd/lib/table/Column';
import React, { useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import settings from '../../settings';
import { generateUuid } from '../../shared/utils';
import {
  ScheduleFrequencies,
  deleteReportScheduleTemplateMutation,
} from './constants';
import PreviewTemplateFormModal from './PreviewTemplateFormModal';

const { Text } = Typography;

const frequencyColors = {
  WEEKLY: 'blue',
  MONTHLY: 'green',
  QUARTERLY: 'orange',
  CUSTOM: 'purple',
};

function ListReportScheduleTemplates({
  loading,
  error,
  templates,
  next,
  hasNextPage,
  refetch,
  onFiltersChanged,
  onSortedByChange,
  onSortOrderChange,
}) {
  const history = useHistory();
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: settings.pageSize,
  });
  const handleTableChange = useCallback((params) => {
    setPagination({
      ...params.pagination,
    });
  }, []);

  const [deleteTemplate] = useMutation(deleteReportScheduleTemplateMutation);
  const [deleting, setDeleting] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const handleClone = useCallback(
    (record) => {
      const cloneId = generateUuid();
      history.push({
        pathname: `/report-schedule-template/edit/${cloneId}`,
        state: {
          mode: 'create',
          cloneTemplate: {
            name: `${record.name} (Copy)`,
            description: record.description || '',
            frequency: record.frequency,
            formFields: (record.formFields || []).map((field) => ({
              ...field,
              fieldId: generateUuid(),
            })),
          },
        },
      });
    },
    [history],
  );

  const handleDelete = useCallback(
    async (_id) => {
      setDeleting(_id);
      try {
        await deleteTemplate({ variables: { _id } });
        notification.success({
          message: 'Deleted',
          description: 'Template deleted successfully',
        });
        refetch();
      } catch (err) {
        console.error(err);
        notification.error({
          message: 'Error',
          description: err?.message || 'Failed to delete template',
        });
      }
      setDeleting(null);
    },
    [deleteTemplate, refetch],
  );

  return (
    <>
      {error && (
        <div className="errors">
          <Text type="danger">{error}</Text>
        </div>
      )}
      <div className="top-actions">
        <Form
          layout="inline"
          form={form}
          style={{ maxWidth: 700 }}
          onValuesChange={onFiltersChanged}
        >
          <Form.Item name="search">
            <Input
              placeholder="Search templates"
              suffix={<SearchOutlined />}
              style={{ minWidth: 250 }}
            />
          </Form.Item>
          <Form.Item name="frequency">
            <Select
              placeholder="Filter by frequency"
              allowClear
              style={{ minWidth: 160 }}
            >
              {Object.values(ScheduleFrequencies).map((c) => (
                <Select.Option key={c.key} value={c.key}>
                  {c.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
        <Button icon={<ReloadOutlined />} onClick={refetch}>
          Refresh
        </Button>
      </div>
      <Table
        dataSource={templates}
        loading={!templates.length && loading}
        pagination={{ ...pagination, total: templates.length }}
        onChange={handleTableChange}
        style={{ width: '100%' }}
        rowKey="_id"
      >
        <Column title="Name" dataIndex="name" key="name" />
        <Column
          title="Description"
          dataIndex="description"
          key="description"
          ellipsis
          render={(text) => text || '-'}
        />
        <Column
          title="Frequency"
          dataIndex="frequency"
          key="frequency"
          render={(frequency) => (
            <Tag color={frequencyColors[frequency] || 'default'}>
              {ScheduleFrequencies[frequency]?.label || frequency}
            </Tag>
          )}
        />
        <Column
          title="Fields"
          dataIndex="formFields"
          key="formFields"
          render={(fields) => (fields ? fields.length : 0)}
        />
        <Column
          title="Lifecycle"
          key="lifecycle"
          render={(text, record) =>
            record.isLocked ? (
              <Tooltip title={record.lockReason || 'This template is locked.'}>
                <Tag color="red" icon={<LockOutlined />}>
                  Locked
                </Tag>
              </Tooltip>
            ) : (
              <Tag color="green">Editable</Tag>
            )
          }
        />
        <Column
          title="Updated"
          dataIndex="updatedAt"
          key="updatedAt"
          render={(text) => (text ? new Date(text).toLocaleDateString() : '-')}
        />
        <Column
          title="Actions"
          key="actions"
          width={200}
          render={(text, record) => (
            <Space>
              <Tooltip title="Preview Form">
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => setPreviewTemplate(record)}
                />
              </Tooltip>
              <Tooltip title={record.isLocked ? 'View (locked)' : 'Edit'}>
                <Link to={`/report-schedule-template/edit/${record._id}`}>
                  <Button type="link" icon={<EditOutlined />} />
                </Link>
              </Tooltip>
              <Tooltip title="Clone">
                <Button
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={() => handleClone(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Popconfirm
                  title="Delete this template?"
                  description={
                    record.isLocked
                      ? record.lockReason ||
                        'Locked templates cannot be deleted.'
                      : 'This action cannot be undone.'
                  }
                  onConfirm={() => handleDelete(record._id)}
                  okText="Delete"
                  cancelText="Cancel"
                  disabled={record.isLocked}
                >
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleting === record._id}
                    disabled={record.isLocked}
                  />
                </Popconfirm>
              </Tooltip>
            </Space>
          )}
        />
      </Table>
      {hasNextPage && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button onClick={next} loading={loading}>
            Load More
          </Button>
        </div>
      )}
      <PreviewTemplateFormModal
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />
      <style jsx>{`
        .top-actions {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 8px;
        }
        .errors {
          margin-bottom: 16px;
        }
      `}</style>
    </>
  );
}

export default ListReportScheduleTemplates;
